import * as _ from 'lodash';
import { Observable, forkJoin, of } from 'rxjs';
import { ListItem } from 'fc-ui-components';
import { map, switchMap, shareReplay, catchError } from 'rxjs/operators';

import { Item, SelectableItem } from 'fc-ui-components';

import { LanguageService } from 'services';
import {
    Season,
    getCurrentSeason,
    getPreviousSeasons,
    getNosSeason,
    seasonTranslationKeyMap,
    deburrAndLower
} from 'helpers';
import { S3File, S3FileModel, ProductCategory } from 'shared/models';
import {
    colorTranslationKeyMap,
    orderAreaTargetGroupTranslationKeyMap,
    Period,
    periodTranslationKeyMap
} from './constants';

export class FiltersProvider {
    private s3Categories: Observable<ProductCategory[]>;
    private s3DiscountBrands: Observable<{ [brandId: string]: number }>;

    constructor(
        private languageService: LanguageService,
        private s3FileModel: S3FileModel
    ) {
        this.s3Categories = this.s3FileModel.getJSON(S3File.ProductCategories).pipe(shareReplay());
        this.s3DiscountBrands = this.s3FileModel.getJSON(S3File.DiscountBrands).pipe(shareReplay());
    }

    public getColorsFilter(selectedColors: string[]): Observable<FilterSelectableItem[]> {
        const colors = _.keys(colorTranslationKeyMap);
        const colorsForFilter: Observable<FilterSelectableItem>[] = _.map(colors, (colorName: string) =>
            this.languageService.translateObservable(colorTranslationKeyMap[colorName]).pipe(
                map((translatedColorName) => ({
                    text: translatedColorName,
                    value: colorName,
                    label: translatedColorName,
                    checked: _.includes(selectedColors, colorName)
                }))
            )
        );

        return forkJoin(colorsForFilter).pipe(map((filters) => _.sortBy(filters, ({ text }) => deburrAndLower(text))));
    }

    public getPeriodsFilter(selectedPeriod: string): Observable<FilterSelectableItem[]> {
        const periods = _.keys(periodTranslationKeyMap) as Period[];
        const periodsForFilter: Observable<FilterSelectableItem>[] = _.map(periods, (periodName: Period) =>
            this.languageService.translateObservable(periodTranslationKeyMap[periodName]).pipe(
                map((translatedPeriodName) => ({
                    text: translatedPeriodName,
                    value: periodName,
                    label: translatedPeriodName,
                    checked: selectedPeriod === periodName
                }))
            )
        );

        return forkJoin(periodsForFilter).pipe(map((filters) => _.sortBy(filters, ({ text }) => deburrAndLower(text))));
    }

    public getTargetGroupsFilter(selectedTargetGroups: string[]): Observable<FilterSelectableItem[]> {
        const targetGroups = _.keys(orderAreaTargetGroupTranslationKeyMap);
        const targetGroupsForFilter: Observable<FilterSelectableItem>[] = _.map(
            targetGroups,
            (targetGroupName: string) =>
                this.languageService.translateObservable(orderAreaTargetGroupTranslationKeyMap[targetGroupName]).pipe(
                    map((translatedTargetGroupName) => ({
                        text: translatedTargetGroupName,
                        value: targetGroupName,
                        label: translatedTargetGroupName,
                        checked: _.includes(selectedTargetGroups, targetGroupName)
                    }))
                )
        );

        return forkJoin(targetGroupsForFilter).pipe(
            map((filters) => _.sortBy(filters, ({ text }) => deburrAndLower(text)))
        );
    }

    public getSeasonsFilter(selectedSeasons: string[]): Observable<FilterSelectableItem[]> {
        const seasons = [getCurrentSeason(), ...getPreviousSeasons(2), getNosSeason()];
        const seasonsForFilter: Observable<FilterSelectableItem>[] = _.map(seasons, (season: Season) =>
            this.languageService.translateObservable(seasonTranslationKeyMap[season.name]).pipe(
                map((translatedSeasonName) => {
                    const value = `${season.name}|${season.year}`;
                    const translatedSeasonNameWithYear = `${translatedSeasonName} ${season.year}`;
                    return {
                        text: translatedSeasonNameWithYear,
                        label: translatedSeasonNameWithYear,
                        value,
                        checked: _.includes(selectedSeasons, value)
                    };
                })
            )
        );
        return forkJoin(seasonsForFilter);
    }

    public getMinimumDiscountFilter(): Item[] {
        const discountValues = [10, 20, 30, 40, 50, 60, 70, 80, 90];
        return _.map(discountValues, (value) => ({ text: `> ${value}%`, value, label: `> ${value}%` }));
    }

    public getMinimumStockFilter(): Observable<Item[]> {
        const stockValues = [5, 10, 20, 50, 100, 200];
        const stockValuesForFilter: Observable<Item>[] = _.map(stockValues, (value: number) =>
            this.languageService
                .translateObservable('ORDER.PRODUCT_SEARCH.MINIMUM_STOCK_ITEM', { value })
                .pipe(
                    map((translatedStockValue) => ({ text: translatedStockValue, value, label: translatedStockValue }))
                )
        );
        return forkJoin(stockValuesForFilter);
    }

    public getBrandFilter(
        brands: { _id: string; name: string }[],
        selectedBrand: string
    ): Observable<BrandFilterItem[]> {
        return this.s3DiscountBrands.pipe(
            map((discountBrands) =>
                _.map(brands, ({ name, _id }) => ({
                    text: name,
                    value: _id,
                    discountValue: discountBrands[_id] || null,
                    checked: selectedBrand === _id
                }))
            ),
            map((brandsFilter) => _.sortBy(brandsFilter, ({ text }) => deburrAndLower(text)))
        );
    }

    public getCategoriesFilter(selectedCategories: string[]): Observable<ListItem[]> {
        const getTranslationsKeys = reduceCategoriesTree((translationsKeys: string[], category: ProductCategory) => [
            ...translationsKeys,
            category.translationKey
        ]);

        const initialProductCategories = this.s3Categories.pipe(
            switchMap(
                (allProductCategories) =>
                    this.languageService.translateObservable(
                        getTranslationsKeys(
                            {
                                translationKey: 'root',
                                subCategories: allProductCategories
                            },
                            []
                        )
                    ),
                (allProductCategories, translations) => ({ allProductCategories, translations })
            ),
            map(({ allProductCategories, translations }) =>
                this.mapProductCategoriesToListItem(allProductCategories, translations)
            ),
            shareReplay(1)
        );

        return initialProductCategories.pipe(
            map((categories) => this.mapToExpandedCategoryTree(categories, selectedCategories))
        );
    }

    public getSelectedCategoryFilters(categoryFilters: ListItem[]) {
        if (!categoryFilters) {
            return;
        }

        const selectedFilters = categoryFilters.map(this.getActiveCategoryFilters.bind(this));
        return _.filter(selectedFilters, (categoryFilter) => !_.isEmpty(categoryFilter));
    }

    public hasDynamicCategory(selectedBrand: string) {
        return this.s3FileModel.getJSON(S3File.OrganizationFeatureFlags).pipe(
            map((featureList) => {
                try {
                    return _.some(featureList.DYNAMIC_BRANDS_CATEGORY_FILTER, (brandId) => brandId === selectedBrand);
                } catch (error) {
                    console.error('Error while retrieving DYNAMIC_BRANDS_CATEGORY_FILTER feature flag configuration', {
                        error
                    });
                    return false;
                }
            }),
            catchError(() => of(false))
        );
    }

    private getActiveCategoryFilters(categoryFilter: ListItem) {
        const { isActive, subList } = categoryFilter;
        if (isActive) return categoryFilter;
        if (_.isEmpty(subList)) return;
        const selectedSubListFilters = this.getSelectedCategoryFilters(subList);
        return !_.isEmpty(subList) ? selectedSubListFilters[0] : undefined;
    }

    private mapProductCategoriesToListItem(productCategories: ProductCategory[], translations): ListItem {
        return mapCategoriesTree((productCategory: ProductCategory) => {
            const getFcKeys = reduceCategoriesTree((fcKeys: string[], category: ProductCategory) => [
                ...fcKeys,
                ...(_.isEmpty(category.subCategories) ? [category.fcKey] : [])
            ]);

            return {
                text: translations[productCategory.translationKey],
                value: getFcKeys(productCategory, [])
            };
        })({ translationKey: 'root', subCategories: productCategories });
    }

    private mapToExpandedCategoryTree(productCategory: ListItem, appliedCategories: string[]): ListItem[] {
        const newTree = mapCategoriesTree((category: ListItem) => {
            const categoryFcKeys = category.value;
            const intersectionSize = _.size(_.intersection(categoryFcKeys, appliedCategories));
            const appliedFcKeysBelongToCategoryFcKeys =
                !!appliedCategories && _.size(appliedCategories) === intersectionSize;

            return {
                text: category.text,
                value: category.value,
                isActive: _.size(categoryFcKeys) === intersectionSize && appliedFcKeysBelongToCategoryFcKeys,
                showSubList: appliedFcKeysBelongToCategoryFcKeys
            };
        }, 'subList')(productCategory);

        return newTree.subList;
    }
}

const reduceCategoriesTree = (mapper) => (categoriesTree, accumulator) =>
    _.reduce(
        categoriesTree.subCategories,
        (acc, currentTree) => reduceCategoriesTree(mapper)(currentTree, acc),
        mapper(accumulator, categoriesTree)
    );

const mapCategoriesTree =
    (mapper, mapChildrenProperty = 'subCategories') =>
    (node) => {
        const newNode = mapper(node);
        if (node.subCategories) {
            newNode[mapChildrenProperty] = _.map(node.subCategories, mapCategoriesTree(mapper, mapChildrenProperty));
        }
        return newNode;
    };

export interface BrandFilterItem extends SelectableItem {
    discountValue: number;
}

export interface FilterSelectableItem extends SelectableItem {
    label: string;
}
